/*
 * FORM NEWCOMER TOUR
 *
 * One tour, charged as a flat fee per group (data-group-price). Above
 * data-group-size participants, the booking is split into full groups, the
 * last one taking the rest (18 = 12 + 6), each with its own date, start time
 * and language. The additional groups take those of the first one until the
 * visitor changes them. Each different starting / finishing point answered
 * "Yes" adds its data-supplement on top of the price.
 *
 * Hidden required fields are skipped by the generic validation of form.js,
 * so showing / hiding a field is enough to make it required or not.
 */

import { parsePrice, formatPrice } from "./price-utils.js";

const MIN_PEOPLE = 1;
const DEFAULT_GROUP_SIZE = 12;
const INVALID_COLOR = "var(--color-red-lcto)";
// Fields each group has, named after the first group and suffixed with the group number for the next ones
const GROUP_FIELDS = ["datetour", "starttimetour", "preferedlangtour"];

// flatpickr keeps its own selected date : setDate keeps the calendar in sync, without firing change
const setFieldValue = (field, value) => {
  if (field._flatpickr) {
    field._flatpickr.setDate(value, false);
  } else {
    field.value = value;
  }
};

const clearField = (field) => {
  if (field.type === "checkbox" || field.type === "radio") {
    field.checked = false;
  } else {
    setFieldValue(field, "");
  }
  field.style.removeProperty("border-color");
  field.style.removeProperty("box-shadow");
};

document.addEventListener("DOMContentLoaded", function () {
  const form = document.getElementById("formNewcomer");
  if (!form) return;

  const priceInput = form.querySelector(".js-price-tour");
  const priceDetail = form.querySelector(".js-price-detail");
  const peopleInput = form.querySelector(".js-tour-people");
  const peopleError = form.querySelector(".js-tour-people-error");
  const groupsWrapper = form.querySelector(".js-tour-groups");
  const groups = Array.from(form.querySelectorAll(".js-tour-group"));
  const groupNotice = form.querySelector(".js-tour-group-notice");
  const groupNoticeText = form.querySelector(".js-tour-group-notice-text");
  const groupCountInput = form.querySelector(".js-nb-groups");

  const groupSize = (groupsWrapper && parseInt(groupsWrapper.dataset.groupSize, 10)) || DEFAULT_GROUP_SIZE;
  const groupPrice = groupsWrapper ? parsePrice(groupsWrapper.dataset.groupPrice) : 0;
  // Maximum for the whole booking (participants per group × groups), rendered as the max attribute of the field
  const maxPeople = peopleInput ? parseInt(peopleInput.max, 10) : NaN;
  const isValidPeople = (value) => !isNaN(value) && value >= MIN_PEOPLE && (isNaN(maxPeople) || value <= maxPeople);

  const groupField = (group, name) => group.querySelector(`[name^="${name}"]`);

  const peopleLabel = (count) => {
    if (!groupsWrapper) return String(count);
    return count === 1 ? groupsWrapper.dataset.labelOne : groupsWrapper.dataset.labelMany.replace("%d", count);
  };

  // 18 participants in groups of 12 = [12, 6], nothing while the number is empty or invalid
  const groupSizes = () => {
    const people = peopleInput ? parseInt(peopleInput.value, 10) : NaN;
    const sizes = [];
    if (!isValidPeople(people)) return sizes;
    for (let left = people; left > 0; left -= groupSize) {
      sizes.push(Math.min(left, groupSize));
    }
    return sizes;
  };

  const groupCount = () => Math.max(groupSizes().length, 1);

  // A group that appears takes the date, start time and language of the first one, in its empty fields only
  const copyFirstGroup = (group) => {
    GROUP_FIELDS.forEach((name) => {
      const source = groupField(groups[0], name);
      const target = groupField(group, name);
      if (source && target && !target.value) setFieldValue(target, source.value);
    });
  };

  const clearGroup = (group) => {
    GROUP_FIELDS.forEach((name) => {
      const field = groupField(group, name);
      if (!field) return;
      clearField(field);
      delete field.dataset.edited;
    });
  };

  const updateGroups = () => {
    const sizes = groupSizes();
    const count = Math.max(sizes.length, 1);

    groups.forEach((group, index) => {
      if (index > 0) {
        const wasVisible = !group.classList.contains("hidden");
        const visible = index < count;
        group.classList.toggle("hidden", !visible);
        if (visible && !wasVisible) copyFirstGroup(group);
        if (!visible && wasVisible) clearGroup(group);
      }

      const countInput = group.querySelector(".js-tour-group-count");
      if (countInput) countInput.value = sizes[index] || "";
      const label = group.querySelector(".js-tour-group-people");
      if (label) label.textContent = sizes[index] ? peopleLabel(sizes[index]) : "";
    });

    // The first group is only titled once the booking is split
    const firstLegend = groups.length ? groups[0].querySelector(".js-tour-group-legend") : null;
    if (firstLegend) firstLegend.classList.toggle("hidden", count < 2);
    if (groupNotice) groupNotice.classList.toggle("hidden", count < 2);
    if (groupNoticeText) {
      groupNoticeText.textContent = count < 2 ? "" : (groupNoticeText.dataset.template || "").replace("[groups]", count);
    }
    if (groupCountInput) groupCountInput.value = count;
  };

  const updatePrice = () => {
    if (!priceInput) return;
    const count = groupCount();
    let total = count * groupPrice;
    form.querySelectorAll(".js-tour-point [data-supplement]:checked").forEach((radio) => {
      total += parsePrice(radio.dataset.supplement);
    });
    priceInput.value = formatPrice(total);

    if (priceDetail) {
      priceDetail.classList.toggle("hidden", count < 2);
      priceDetail.textContent = (priceDetail.dataset.template || "").replace("%d", count).replace("%s", formatPrice(groupPrice));
    }
  };

  // An empty field is left to the generic required-field validation of form.js
  const validatePeople = () => {
    if (!peopleInput) return true;
    const value = parseInt(peopleInput.value, 10);
    const invalid = peopleInput.value.trim() !== "" && !isValidPeople(value);
    if (peopleError) peopleError.classList.toggle("hidden", !invalid);
    if (invalid) {
      peopleInput.style.setProperty("border-color", INVALID_COLOR, "important");
    } else {
      peopleInput.style.removeProperty("border-color");
    }
    return !invalid;
  };

  if (peopleInput) {
    ["input", "change"].forEach((evt) => {
      peopleInput.addEventListener(evt, () => {
        validatePeople();
        updateGroups();
        updatePrice();
      });
    });
  }

  if (groups.length) {
    // A field changed by the visitor in an additional group no longer follows the first group
    groups.slice(1).forEach((group) => {
      GROUP_FIELDS.forEach((name) => {
        const field = groupField(group, name);
        if (!field) return;
        ["input", "change"].forEach((evt) => {
          field.addEventListener(evt, () => {
            field.dataset.edited = "1";
          });
        });
      });
    });

    GROUP_FIELDS.forEach((name) => {
      const source = groupField(groups[0], name);
      if (!source) return;
      source.addEventListener("change", () => {
        groups.slice(1).forEach((group) => {
          const target = groupField(group, name);
          if (target && !group.classList.contains("hidden") && !target.dataset.edited) {
            setFieldValue(target, source.value);
          }
        });
      });
    });
  }

  form.addEventListener("submit", function (e) {
    if (!validatePeople()) {
      e.preventDefault();
      e.stopPropagation();
      peopleInput.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  });

  // Booking as an individual : no company name nor VAT details
  const individual = form.querySelector('[name="individual"]');
  if (individual) {
    const toggleCompanyFields = () => {
      form.querySelectorAll(".js-company-field").forEach((wrapper) => {
        wrapper.classList.toggle("hidden", individual.checked);
        if (individual.checked) {
          wrapper.querySelectorAll("input").forEach(clearField);
        }
      });
      // "Registered for VAT" is unticked above, so the VAT ID goes back to its initial hidden state
      if (individual.checked) {
        form.querySelectorAll(".field-vatnumber").forEach((field) => {
          field.classList.add("hidden");
          field.required = false;
        });
      }
    };

    individual.addEventListener("change", toggleCompanyFields);
    toggleCompanyFields();
  }

  // Different starting / finishing point : the preferred point is only asked with "Yes"
  form.querySelectorAll(".js-tour-point").forEach((point) => {
    const detail = point.querySelector(".js-tour-point-detail");
    if (!detail) return;

    const toggleDetail = () => {
      const wantsChange = !!point.querySelector('input[type="radio"][value="yes"]:checked');
      detail.classList.toggle("hidden", !wantsChange);
      if (!wantsChange) {
        detail.querySelectorAll("input").forEach(clearField);
      }
    };

    point.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.addEventListener("change", () => {
        toggleDetail();
        updatePrice();
      });
    });

    toggleDetail();
  });

  updateGroups();
  updatePrice();
});
